import React, {Fragment, useRef, useState} from "react";
import {Menu, Transition} from "@headlessui/react";
import classNames from "classnames";
import ArrowDownIcon from "../icon/ArrowDownIcon";
import {useSetRecoilState} from "recoil";
import localeState from "../../recoil/locale";
import useLoginUser from "../../query/auth/useLoginUser";
import {errorToast} from "../../toast";
import {User} from "../../model/user";
import {API_HOST, WEB_HOST} from "../../core/variables";
import AccountSettingIcon from "../icon/AccountSettingIcon";
import {useIntl} from "react-intl";
import ConfirmDialog , {getConfirmDialogId} from "../ConfirmDialog";
import useModal from "../../hook/useModal";
import {HomeContentId, HomeContentScrollContainer} from "./variable/HomeContentType";
import HomeAccountSettingModal, {HOME_ACCOUNT_SETTING_MODAL_ID} from "./HomeAccountSettingModal";
import {scrollToElement} from "../../util/animateScroll";
import ListIcon from "../icon/ListIcon";
import Logo from "../Logo";
import UserIcon from "../icon/UserIcon";
import useLinkHandler from "../../hook/useLinkHandler";

const HOME_SIGN_UP_MODAL = "home-sign-up-modal"
export const HOME_LOGOUT_MODAL = "home-logout-modal-id";

const HomeMenu: React.FC = () => {
    const intl = useIntl();
    const setLocale = useSetRecoilState(localeState);
    const {data: profile} = useLoginUser();

    const smoothScroll = (to: string) => {
        const element = document.querySelector(to);
        const scrollContainer = document.querySelector(`#${HomeContentScrollContainer}`)!;
        element && scrollToElement(element, scrollContainer);
    }

    const onClickLogo = () => {
        smoothScroll(`#${HomeContentId.HERO}`);
    }

    const onClickIntroduction = () => {
        smoothScroll(`#${HomeContentId.HERO}`);
    }

    const onClickSubscription = () => {
        smoothScroll(`#${HomeContentId.SUBSCRIPTION}`)
    }

    const onClickUserGuide = () => {
        errorToast(intl.formatMessage({id: "i0052"}))
    }

    const onClickDevDocument = () => {
        errorToast(intl.formatMessage({id: "i0053"}))
    }

    const onClickKorean = () => {
        setLocale("ko")
    }

    const onClickEnglish = () => {
        setLocale("en")
    }

    return (
        <div className={classNames("min-w-fit fixed top-0 left-0 right-0 bg-[#FFFFFF] h-[68px] px-[24px] py-[18px]",
            "flex items-center break-keep z-30")}>
            <div className="flex items-center gap-x-[40px]">
                <Logo onClick={onClickLogo} btnClassNames={"w-fit"} />
                <div className="flex items-center gap-x-[72px]">
                    <div className="cursor-pointer whitespace-nowrap text-[15px]" onClick={onClickIntroduction}>
                        {intl.formatMessage({id: "i0000"})}
                    </div>
                    <div className="cursor-pointer whitespace-nowrap text-[15px]" onClick={onClickSubscription}>
                        {intl.formatMessage({id: "i0001"})}
                    </div>
                    <HomeMenuDropDown title={intl.formatMessage({id: "i0002"})}
                                      btnClassName={"flex items-center gap-x-[8px] whitespace-nowrap"}>
                        <HomeMenuDropDownItem title={intl.formatMessage({id: "i0003"})} onClick={onClickUserGuide} />
                        <HomeMenuDropDownItem title={intl.formatMessage({id: "i0004"})} onClick={onClickDevDocument} />
                    </HomeMenuDropDown>
                    <HomeMenuDropDown title={intl.formatMessage({id: "i0005"})}
                                      btnClassName={"flex items-center gap-x-[8px] whitespace-nowrap"}>
                        <HomeMenuDropDownItem title={intl.formatMessage({id: "i0006"})} onClick={onClickKorean} />
                        <HomeMenuDropDownItem title={intl.formatMessage({id: "i0007"})} onClick={onClickEnglish} />
                    </HomeMenuDropDown>
                </div>
            </div>

            <div className="ml-auto pl-[64px] w-fit">
                {profile ? <HomeProfileMenu profile={profile} /> : <HomeAuthenticationMenu />}
            </div>
        </div>
    )
}


type HomeMenuDropDownItemProps = {
    title: string;
    onClick: () => void
}

const HomeMenuDropDownItem: React.FC<HomeMenuDropDownItemProps> = (props) => {
    const {title, onClick} = props

    return (
        <Menu.Item as={"div"}
                   className={classNames("min-w-[112px] px-[17px] pt-[8px] pb-[7px] h-[32px] cursor-pointer hover:bg-bunny-primary-100",
                       "text-console-black text-[14px] font-medium focus:text-bunny-default focus:font-semibold whitespace-nowrap")}
                   onClick={onClick}>
            {title}
        </Menu.Item>
    )
};

type HomeMenuDropDownProps = {
    title: string;
    btnClassName?: string;
    children: React.ReactNode
}

const HomeMenuDropDown: React.FC<HomeMenuDropDownProps> = (props) => {
    const {title, btnClassName, children} = props;

    const [isHover, setIsHover] = useState<boolean>(false);

    return (
        <Menu>
            {({open}) => (
                <div className={"relative"}>
                    <Menu.Button className={classNames(btnClassName ?? "")}
                                 onMouseEnter={() => setIsHover(true)}
                                 onMouseLeave={() => setIsHover(false)}
                    >
                        <span className="text-left text-[15px] leading-none">
                            {title}
                        </span>
                        <div className={"w-[16px]"}>
                            <ArrowDownIcon className={"fill-console-black"}/>
                        </div>
                    </Menu.Button>
                    <div className={classNames((isHover || open) && "will-change-transform")}>
                        <Transition as={Fragment}
                                    enter="transition duration-300 ease-out"
                                    enterFrom="translate-y-[26px] opacity-0 translate-x-[-4px]"
                                    enterTo="translate-y-[10px] opacity-100 translate-x-[-4px]"
                                    leave="transition duration-300 ease-out"
                                    leaveFrom="translate-y-[10px] opacity-100 translate-x-[-4px]"
                                    leaveTo="translate-y-[26px] opacity-0 translate-x-[-4px]"
                        >
                            <Menu.Items className={"absolute z-20 w-fit rounded-[6px] py-[10px] border border-ob-bunny-border-gray bg-white drop-shadow-[0px_2px_4px_rgba(0,0,0,0.2)]"}>
                                {children}
                            </Menu.Items>
                        </Transition>
                    </div>
                </div>
            )}
        </Menu>
    )
}


const HomeAuthenticationMenu: React.FC = () => {
    const intl = useIntl();
    const modal = useModal();
    const { linkToLogin, linkToOnboarding, linkToSignUp } = useLinkHandler()

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const onClickLogin = () => {
        linkToLogin();
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const onClickSignUp = () => {
        modal.open(getConfirmDialogId(HOME_SIGN_UP_MODAL));
    }

    return (
        <div className="flex items-center gap-x-[8px] h-full">
            {/*<div className={classNames("w-[86px] h-[39px] cursor-pointer py-[10px] px-[16px]",*/}
            {/*    "border border-bunny-gray-400 rounded-[6px] flex justify-center items-center")}*/}
            {/*     onClick={onClickSignUp}>*/}
            {/*    <div className={"whitespace-nowrap text-console-black text-[14px] font-normal"}>*/}
            {/*        {intl.formatMessage({id: "i0008"})}*/}
            {/*    </div>*/}
            {/*</div>*/}
            {/*<div className={classNames("w-[84px] h-[37px] cursor-pointer py-[10px] px-[16px]",*/}
            {/*    "border border-bunny-gray-400 rounded-[6px] flex justify-center items-center")}*/}
            {/*     onClick={onClickLogin}>*/}
            {/*    <div className={"text-console-black text-[14px] font-normal"}>*/}
            {/*        {intl.formatMessage({id: "i0009"})}*/}
            {/*    </div>*/}
            {/*</div>*/}

            <ConfirmDialog dialogId={HOME_SIGN_UP_MODAL}
                           content={intl.formatMessage({id: "i0056"})}
                           confirmVariant={"primary"}
                           onConfirm={() => linkToOnboarding()}
                           confirmText={intl.formatMessage({id: "i0060"})}
                           cancelText={intl.formatMessage({id: "i0061"})}
                           onCancel={() => linkToSignUp()}
                           showCancelButton={true}
                           closeTriggerOnCancel={false}
            />
        </div>
    )
}

const HomeProfileMenu: React.FC<{profile: User}> = ({profile}) => {
    const intl = useIntl();
    const modal = useModal();
    const { linkToChannelList } = useLinkHandler()

    const onClickAccountSetting = () => {
        modal.open(HOME_ACCOUNT_SETTING_MODAL_ID);
    }

    const onClickChannelList = () => {
        linkToChannelList();
    }

    const onClickLogOut = () => {
        modal.open(getConfirmDialogId(HOME_LOGOUT_MODAL));
    }

    return (
        <div className="flex items-center gap-x-[8px] h-full">
            <ProfileDropDown profile={profile}>
                <HomeProfileMenuItem disabled={true} btnClassName={"py-[20px] h-[64px] pointer-events-none"}>
                    <UserIcon fill={"#441A98"} className="w-[24px] y-[24px]" />
                    <div className={"text-bunny-default font-semibold text-[16px]"}>
                        {profile.fullName}
                    </div>
                </HomeProfileMenuItem>
                <HomeProfileMenuItem onClick={onClickAccountSetting}>
                    {({isFocus}) => (
                        <>
                            <AccountSettingIcon fill={isFocus ? "#63469B" : "#37352F"} className="w-[20px] y-[20px]" />
                            <div className={classNames("text-[14px]", isFocus ? "font-semibold text-bunny-default" : "font-normal text-console-black")}>
                                {intl.formatMessage({id: "i0010"})}
                            </div>
                        </>
                    )}
                </HomeProfileMenuItem>
                <HomeProfileMenuItem onClick={onClickChannelList}>
                    {({isFocus}) => (
                        <>
                            <ListIcon fill={isFocus ? "#63469B" : "#37352F"} className="w-[20px] y-[20px]" />
                            <div className={classNames("text-[14px]", isFocus ? "font-semibold text-bunny-default" : "font-normal text-console-black")}>
                                {intl.formatMessage({id: "i0011"})}
                            </div>
                        </>
                    )}
                </HomeProfileMenuItem>
                <HomeProfileMenuItem onClick={onClickLogOut}>
                    {({isFocus}) => (
                        <div className={classNames("text-[14px]", isFocus ? "font-semibold text-bunny-default" : "font-normal text-console-black")}>
                            {intl.formatMessage({id: "i0012"})}
                        </div>
                    )}
                </HomeProfileMenuItem>
            </ProfileDropDown>

            <HomeAccountSettingModal />

            <ConfirmDialog dialogId={HOME_LOGOUT_MODAL}
                           content={intl.formatMessage({id: "i0085"})}
                           showCancelButton={true}
                           confirmVariant={"primary"}
                           confirmText={intl.formatMessage({id: "i0086"})}
                           onConfirm={() => {window.location.href = `${API_HOST}/auth/logout?redirect_uri=${WEB_HOST}`}}
            />
        </div>
    )
}

type ProfileDropDownProps = {
    children: React.ReactNode;
    profile: User;
}

const ProfileDropDown: React.FC<ProfileDropDownProps> = (props) => {
    const {children, profile} = props;

    const btnRef = useRef<HTMLButtonElement>(null)
    const [isHover, setIsHover] = useState<boolean>(false);

    return (
        <Menu>
            {({open}) => {
                if (!open) {
                    btnRef.current?.blur();
                }

                return (
                    <div className={"relative z-20"}>
                        <Menu.Button className={classNames("min-w-fit h-[40px] cursor-pointer px-[14px] py-[8px]",
                            "rounded-[20px] flex gap-x-[8px] justify-center items-center",
                            "bg-[#FFFFFF] focus:bg-bunny-primary-300", open && "bg-bunny-primary-300")}
                                     ref={btnRef}
                                     onMouseEnter={() => setIsHover(true)}
                                     onMouseLeave={() => setIsHover(false)}
                        >
                            <UserIcon fill={(isHover && !open) ? "#63469B" : "#441A98"} className="w-[24px] y-[24px]" />
                            <div className={classNames("whitespace-nowrap text-[16px] leading-[24px] font-semibold",
                                (isHover && !open) ? "text-bunny-primary-700" : "text-bunny-default")}>
                                {profile.fullName}
                            </div>
                        </Menu.Button>
                        <div className={classNames((isHover || open) && "will-change-transform")}>
                            <Transition as={Fragment}
                                        enter="transition duration-300 ease-out"
                                        enterFrom="translate-y-0 opacity-0"
                                        enterTo="translate-y-[18px] opacity-100"
                                        leave="transition duration-300 ease-out"
                                        leaveFrom="translate-y-[18px] opacity-100"
                                        leaveTo="translate-y-0 opacity-0"
                            >
                                <Menu.Items className={classNames("absolute right-0 rounded-[6px] w-[280px]",
                                    "border border-bunny-gray-300 overflow-hidden")}>
                                    {children}
                                </Menu.Items>
                            </Transition>
                        </div>
                    </div>
                )
            }}
        </Menu>
    )
}


type HomeProfileMenuItemProps = {
    onClick?: () => void,
    disabled?: boolean,
    btnClassName?: string,
    children: React.ReactNode | ((props: { isFocus: boolean }) => React.ReactNode)
}

const HomeProfileMenuItem: React.FC<HomeProfileMenuItemProps> = (props) => {
    const { onClick, disabled = false, btnClassName, children} = props;
    const [isFocus, setIsFocus] = useState<boolean>(false)

    return (
        <Menu.Item as={"div"}
                   disabled={disabled}
                   onFocus={() => setIsFocus(true)}
                   onBlur={() => setIsFocus(false)}
                   className={classNames("flex items-center py-[15px] bg-white pl-[17px] w-full h-[46px] cursor-pointer",
                       "border-b border-bunny-gray-300 gap-x-[12px] hover:bg-bunny-primary-100", btnClassName ?? "")}
                   onClick={onClick}>
            {typeof children === 'function' ? children({ isFocus }) : children}
        </Menu.Item>
    )
}

export default HomeMenu;
